import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { 
  Gift,
  Percent,
  Star,
  Truck,
  Check,
  CheckCircle,
  Info,
  XCircle,
  X,
  Code,
  Eye,
  Activity
} from 'lucide-react'
import { useApiLogs } from '../contexts/ApiLogsContext'
import DemoTutorial from '../components/DemoTutorial'

// Environment variables
const API_BASE_URL = 'https://api.adspostx.com'

interface ApiRequest {
  url: string
  method: string
  headers?: Record<string, string>
  body?: any
  timestamp?: string
}

interface ApiResponse {
  status: number
  statusText: string
  headers: Record<string, string>
  data: any
  timestamp: string
}

interface ApiLog {
  page: string
  action: string
  request: ApiRequest
  response: ApiResponse | null
  loading?: boolean
  error?: string | null
}

// Create a type for the different step states
// Note: Currently using 'initial' state only

// Define category icons for the offers

interface Perk {
  id: string
  title: string
  description: string
  value: string
  image: string | null
  category: 'shipping' | 'discount' | 'gift' | 'points'
  icon: React.ComponentType<any>
  selected: boolean
  sessionId?: string
  campaignId?: string
  offerId?: string
}

interface Toast {
  id: string
  type: 'success' | 'error' | 'info'
  message: string
  duration?: number
  persist?: boolean
}

const USPCancellationDemoPage: React.FC = () => {
  const navigate = useNavigate()
  const { addApiLog, updateApiLog, addSelectionHistory, apiLogs: contextApiLogs, selectionHistory: contextSelectionHistory } = useApiLogs()
  
  // Auth state
  const [apiKey, setApiKey] = useState('')
  const [pubUserId, setPubUserId] = useState('')
  const [sessionId, setSessionId] = useState('')
  
  // UI state
  // Removed dark mode support for MomentScience branding
  const [toasts, setToasts] = useState<Toast[]>([])
  const [showTutorial, setShowTutorial] = useState(false)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [confirmationModalData, setConfirmationModalData] = useState<{
    type: 'keep-account' | 'complete-cancellation'
    selectedCount: number
  } | null>(null)
  
  // Data state
  const [selectedPerks, setSelectedPerks] = useState<Perk[]>([])
  // @ts-ignore: perks is used in autoSelectAllOffers function but TypeScript doesn't detect it
  const [perks, setPerks] = useState<Perk[]>([])
  const [perksLoading, setPerksLoading] = useState(false)
  const [perksError, setPerksError] = useState<string | null>(null)
  const [uspAllOffersChecked, setUspAllOffersChecked] = useState<boolean | null>(null)
  
  // API state
  const [sessionDetailsLog, setSessionDetailsLog] = useState<ApiLog | null>(null)
  
  // Sidebar states
  const [showApiPanel, setShowApiPanel] = useState(false)
  const [activeTab, setActiveTab] = useState('session')
  const [sidebarWidth, setSidebarWidth] = useState(480)
  const [isResizing, setIsResizing] = useState(false)

  // Resize functionality for sidebar
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsResizing(true)
    e.preventDefault()
  }

  const handleMouseMove = (e: MouseEvent) => {
    if (!isResizing) return
    
    const newWidth = window.innerWidth - e.clientX
    const minWidth = 320
    const maxWidth = window.innerWidth * 0.7
    
    const constrainedWidth = Math.max(minWidth, Math.min(maxWidth, newWidth))
    setSidebarWidth(constrainedWidth)
  }

  const handleMouseUp = () => {
    setIsResizing(false)
  }

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = 'ew-resize'
      document.body.style.userSelect = 'none'
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
    }
  }, [isResizing])

  // Initialization effects
  useEffect(() => {
    // Load credentials
    const storedApiKey = sessionStorage.getItem('usp_api_key')
    const storedPubUserId = sessionStorage.getItem('usp_pub_user_id')
    
    if (!storedApiKey || !storedPubUserId) {
      navigate('/usp-setup')
      return
    }
    
    setApiKey(storedApiKey)
    setPubUserId(storedPubUserId)

    // MomentScience uses light theme only
  }, [navigate])

  // Fetch offers when credentials are ready
  useEffect(() => {
    if (apiKey && pubUserId) {
      fetchOffersAndRestoreState()
    }
  }, [apiKey, pubUserId])

  // Tutorial effect
  useEffect(() => {
    const tutorialSeen = localStorage.getItem('usp_tutorial_seen')
    setShowTutorial(!tutorialSeen)
  }, [])

  // Helper functions

// Load credentials on mount
useEffect(() => {
  const storedApiKey = sessionStorage.getItem('usp_api_key')
  const storedPubUserId = sessionStorage.getItem('usp_pub_user_id')
  
  if (!storedApiKey || !storedPubUserId) {
    navigate('/usp-setup')
    return
  }
  
  setApiKey(storedApiKey)
  setPubUserId(storedPubUserId)
}, [navigate])

// Load theme on mount
// MomentScience uses light theme only

  // Helper functions
  const getOfferCategory = (perk: Perk): 'shipping' | 'discount' | 'gift' | 'points' => {
    // Determine category based on offer content
    const title = perk.title?.toLowerCase() || ''
    const desc = perk.description?.toLowerCase() || ''
    
    if (title.includes('shipping') || title.includes('delivery') || desc.includes('shipping')) {
      return 'shipping'
    } else if (title.includes('%') || title.includes('discount') || title.includes('off') || desc.includes('discount')) {
      return 'discount'
    } else if (title.includes('gift') || title.includes('bonus') || desc.includes('gift')) {
      return 'gift'
    } else {
      return 'points'
    }
  }

  const getOfferIcon = (category: 'shipping' | 'discount' | 'gift' | 'points') => {
    switch (category) {
      case 'shipping': return Truck
      case 'discount': return Percent
      case 'gift': return Gift
      case 'points': return Star
      default: return Star
    }
  }

  const getCategoryColor = (category: 'shipping' | 'discount' | 'gift' | 'points') => {
    switch (category) {
      case 'shipping': return 'bg-secondary-100 text-secondary-600'
      case 'discount': return 'bg-green-100 text-green-600'
      case 'gift': return 'bg-purple-100 text-purple-600'
      case 'points': return 'bg-primary-100 text-primary-600'
      default: return 'bg-gray-100 text-gray-600'
    }
  }

  const showToast = (
    message: string, 
    type: 'success' | 'error' | 'info', 
    duration = 5000, 
    persist = false
  ) => {
    const id = Date.now().toString()
    setToasts(toasts => [...toasts, { id, message, type, duration, persist }])

    if (!persist) {
      setTimeout(() => {
        setToasts(toasts => toasts.filter(t => t.id !== id))
      }, duration)
    }
  }

  const dismissToast = (id: string) => {
    setToasts(toasts => toasts.filter(t => t.id !== id))
  }

  // Main function to fetch offers or restore from session
  const fetchOffersAndRestoreState = async (forceNewSession = false) => {
    // Check if there's a stored session ID
    const storedSessionId = sessionStorage.getItem('usp_session_id')
    
    if (storedSessionId && !forceNewSession) {
      await fetchSessionDetailsAndRestoreState(storedSessionId)
    } else {
      // Remove any existing session ID if forcing new
      if (forceNewSession) {
        sessionStorage.removeItem('usp_session_id')
      }
      await fetchOffers()
    }
  }

  // Function to fetch session details and restore UI state
  const fetchSessionDetailsAndRestoreState = async (sessionIdToUse: string) => {
    setPerksLoading(true)
    setPerksError(null)
    
    try {
      // Call the Get Session Details API
      const sessionDetailsResponse = await fetch(
        `https://api.adspostx.com/sdk/v4/usp/session/${sessionIdToUse}.json?pub_user_id=${pubUserId}`, 
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${apiKey}`
          }
        }
      )

      if (!sessionDetailsResponse.ok) {
        console.log('Session invalid or expired, fetching new offers')
        // If session is invalid or expired, remove from storage and create new one
        sessionStorage.removeItem('usp_session_id')
        await fetchOffers()
        return
      }

      const sessionDetailsData = await sessionDetailsResponse.json()
      
      // Process the session details to reconstruct the UI state
      if (sessionDetailsData && sessionDetailsData.data) {
        // Check for the campaigns in session.campaigns structure (new API format)
        const sessionData = sessionDetailsData.data.session || sessionDetailsData.data
        const campaigns = sessionData.campaigns || []
        const selectedOfferIds = sessionData.selected || []
        
        if (campaigns.length === 0) {
          console.log('No campaigns in session, fetching new offers')
          sessionStorage.removeItem('usp_session_id')
          await fetchOffers()
          return
        }
        
        // Map campaigns to UI perks format
        const uiPerks = campaigns.map((campaign: any, index: number) => {
          // Create a temporary perk object for category determination
          const tempPerk = {
            title: campaign.title || campaign.short_headline || `Offer ${index + 1}`,
            description: campaign.description || campaign.short_description || 'Special offer available'
          } as Perk;
          
          const category = getOfferCategory(tempPerk);
          
          return {
            id: `api-offer-${index}`,
            title: tempPerk.title,
            description: tempPerk.description,
            value: campaign.advertiser_name ? `Provided by ${campaign.advertiser_name}` : 'Exclusive offer',
            image: campaign.image || (campaign.creatives && campaign.creatives.length > 0 ? 
                   campaign.creatives.find((c: any) => c.is_primary)?.url || campaign.creatives[0].url : null),
            category,
            selected: selectedOfferIds.includes(campaign.campaign_id),
            sessionId: sessionIdToUse,
            campaignId: campaign.campaign_id,
            offerId: campaign.id
          }
        })
        
        // Update UI with restored perks
        setSelectedPerks(uiPerks)
        setPerksLoading(false)
        
        // Check if usp_all_offers_checked is set
        if (sessionData.settings && sessionData.settings.usp_all_offers_checked) {
          setUspAllOffersChecked(true)
          
          // Show message that all offers are automatically checked
          showToast('All offers automatically selected based on your preferences!', 'info', 8000, false)
          
          // Auto-select all offers if they aren't already selected
          const hasUnselectedOffers = uiPerks.some((perk: Perk) => !perk.selected)
          if (hasUnselectedOffers) {
            // Update the perks to be selected
            const updatedPerks = uiPerks.map((perk: any) => ({ ...perk, selected: true }))
            setSelectedPerks(updatedPerks)
            setPerks(updatedPerks)
            
            // Call API to actually select them
            setTimeout(() => {
              autoSelectAllOffers(updatedPerks, sessionIdToUse)
            }, 500)
          }
        } else {
          setUspAllOffersChecked(false)
        }
        
        // Note: setApiOffersCount removed - campaigns.length not tracked locally
      } else {
        // If no data in response, fetch new offers
        console.log('No data in session response, fetching new offers')
        sessionStorage.removeItem('usp_session_id')
        await fetchOffers()
      }
    } catch (error) {
      console.error('Error retrieving session:', error)
      // On error, fallback to creating a new session
      sessionStorage.removeItem('usp_session_id')
      await fetchOffers()
    }
  }

  // Track navigation source
  useEffect(() => {
    // Clear any existing session when coming from setup
    const navigationSource = sessionStorage.getItem('usp_navigation_source');
    if (navigationSource === 'setup_page') {
      // Clear the navigation source flag and force new session
      sessionStorage.removeItem('usp_navigation_source');
      sessionStorage.removeItem('usp_session_id');
      console.log('🧹 Cleared session storage for fresh start from setup page');
    }
  }, []);

  // MomentScience uses light theme only

  const fetchOffers = async () => {
    setPerksLoading(true)
    setPerksError(null)
    
    try {
      const requestBody = {
        pub_user_id: pubUserId,
        country: 'us',
        dev: 0
      }

      console.log('🚀 Fetching offers with:', requestBody)
      console.log('🔑 API Key in URL:', apiKey)

      // Add to shared API logs
      const logId = addApiLog({
        page: 'usp-cancellation-demo',
        action: 'POST /native/v2/offers.json',
        request: {
          url: `${API_BASE_URL}/native/v2/offers.json?api_key=${apiKey}`,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: requestBody,
          timestamp: new Date().toISOString()
        },
        response: null,
        loading: true,
        error: null
      })

      const response = await fetch(`${API_BASE_URL}/native/v2/offers.json?api_key=${apiKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      })

      console.log('📡 Response status:', response.status, response.statusText)

      // Try to parse response regardless of status for better error messages
      let data
      try {
        data = await response.json()
        console.log('📦 Response data:', data)
      } catch (parseError) {
        console.error('Failed to parse response as JSON:', parseError)
        throw new Error(`API returned status ${response.status} with invalid JSON response`)
      }

      // Update API log with response
      updateApiLog(logId, {
        response: {
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries()),
          data,
          timestamp: new Date().toISOString()
        },
        loading: false,
        error: response.ok ? null : data.message || `Request failed with status ${response.status}`
      })

      // Check if response is ok after logging
      if (!response.ok) {
        const errorMessage = data.message || data.error || `API request failed with status ${response.status}`
        throw new Error(errorMessage)
      }

      // Process offers if available
      if (data.data?.offers && Array.isArray(data.data.offers) && data.data.offers.length > 0) {
        // Extract session ID from the API response
        const apiSessionId = data.data.session_id || data.session_id

        console.log('✅ Successfully fetched', data.data.offers.length, 'offers')
        console.log('🔑 Session ID:', apiSessionId)

        // Save session ID to state and sessionStorage for persistence
        if (apiSessionId) {
          sessionStorage.setItem('usp_session_id', apiSessionId)
          setSessionId(apiSessionId)
        }

        // Map offers to perks
        const apiPerks = data.data.offers.map((offer: any, index: number) => {
          // Create a temporary perk object for category determination
          const tempPerk = {
            title: offer.title || offer.name || offer.creative_name || `Offer ${index + 1}`,
            description: offer.description || offer.desc || offer.creative_desc || 'Special offer available'
          } as Perk;
          
          const category = getOfferCategory(tempPerk);
          
          return {
            id: `api-offer-${index}`,
            title: tempPerk.title,
            description: tempPerk.description,
            value: offer.advertiser_name ? `Provided by ${offer.advertiser_name}` : 'Exclusive offer',
            image: offer.image || offer.creative_image || offer.icon_url || null,
            category,
            selected: false,
            sessionId: apiSessionId,
            campaignId: offer.campaign_id || offer.campaignId || offer.id,
            offerId: offer.offer_id || offer.offerId || offer.id
          }
        })
        
        // Update UI with offers
        setSelectedPerks(apiPerks.slice(0, 4))
        setPerks(apiPerks.slice(0, 4))
        setPerksError(null)
        
        // Check if usp_all_offers_checked is set in the response
        if (data.data?.settings?.usp_all_offers_checked || data.settings?.usp_all_offers_checked) {
          setUspAllOffersChecked(true)
          
          // Auto-select all offers
          const offersToAutoSelect = apiPerks.slice(0, 4).map((perk: Perk) => ({ ...perk, selected: true }))
          setSelectedPerks(offersToAutoSelect)
          setPerks(offersToAutoSelect)
          
          // Call API to actually select them
          setTimeout(() => {
            autoSelectAllOffers(offersToAutoSelect, apiSessionId)
          }, 500)
        } else {
          setUspAllOffersChecked(false)
        }
      } else {
        console.warn('⚠️ No offers in response')
        setSelectedPerks([])
        setPerksError('No offers available at this time')
      }
    } catch (error) {
      console.error('❌ Error fetching offers:', error)
      const errorMsg = error instanceof Error ? error.message : 'Failed to fetch offers'
      setPerksError(errorMsg)
      setSelectedPerks([])
    } finally {
      setPerksLoading(false)
    }
  }

  const togglePerk = async (perkId: string) => {
    const perk = selectedPerks.find(p => p.id === perkId)
    if (!perk) return

    // If selecting the perk (not deselecting)
    if (!perk.selected) {
      // Add to shared API logs
      const selectLogId = addApiLog({
        page: 'usp-cancellation-demo',
        action: 'POST /sdk/v4/usp/{sessionId}/{campaignId}/select.json',
        request: {
          url: `https://api.adspostx.com/sdk/v4/usp/${perk.sessionId}/${perk.campaignId}/select.json`,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${apiKey}`
          },
          body: { pub_user_id: pubUserId },
          timestamp: new Date().toISOString()
        },
        response: null,
        loading: true,
        error: null
      })

      try {
        console.log('🎯 Calling Select Offer API for perk:', perk.title)
        console.log('Session ID:', perk.sessionId)
        console.log('Campaign ID:', perk.campaignId)
        console.log('Offer ID:', perk.offerId)

        // Generate curl command for this request
        const selectCurlCommand = `curl -X POST \\
  "https://api.adspostx.com/sdk/v4/usp/${perk.sessionId}/${perk.campaignId}/select.json" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -d '${JSON.stringify({ pub_user_id: pubUserId }, null, 2)}'`

        // Call the Select Offer API
        const selectResponse = await fetch(`https://api.adspostx.com/sdk/v4/usp/${perk.sessionId}/${perk.campaignId}/select.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${apiKey}`
          },
          body: JSON.stringify({
            pub_user_id: pubUserId
          })
        })

        const selectData = await selectResponse.json()
        console.log('✅ Select Offer API Response:', selectData)
        console.log('Response Status:', selectResponse.status, selectResponse.statusText)
        
        // Update shared API logs with response
        updateApiLog(selectLogId, {
          response: {
            status: selectResponse.status,
            statusText: selectResponse.statusText,
            headers: Object.fromEntries(selectResponse.headers.entries()),
            data: selectData,
            timestamp: new Date().toISOString()
          },
          loading: false,
          error: null
        })
        
        if (selectResponse.ok) {
          console.log('🎉 Offer successfully selected!')
          showToast(`${perk.title} selected successfully!`, 'success', 10000, true)

          // Add to shared selection history
          addSelectionHistory({
            page: 'usp-cancellation-demo',
            action: 'select',
            perkTitle: perk.title,
            perkId: perk.id,
            campaignId: perk.campaignId,
            status: 'success',
            response: selectData,
            curlCommand: selectCurlCommand
          })
        } else {
          console.error('❌ Select Offer API failed:', selectData)
          showToast('Failed to select offer. Please try again.', 'error', 10000, true)

          // Add to shared selection history
          addSelectionHistory({
            page: 'usp-cancellation-demo',
            action: 'select',
            perkTitle: perk.title,
            perkId: perk.id,
            campaignId: perk.campaignId,
            status: 'error',
            error: selectData.message || 'API request failed',
            curlCommand: selectCurlCommand
          })
          return
        }
      } catch (error) {
        console.error('❌ Error calling Select Offer API:', error)
        const errorMessage = error instanceof Error ? error.message : 'Unknown error'
        showToast('Error selecting offer. Please try again.', 'error')
        
        // Update shared API logs with error
        updateApiLog(selectLogId, {
          response: null,
          loading: false,
          error: errorMessage
        })
        
        // Generate curl command for error case
        const selectCurlCommand = `curl -X POST \\
  "https://api.adspostx.com/sdk/v4/usp/${perk.sessionId}/${perk.campaignId}/select.json" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -d '${JSON.stringify({ pub_user_id: pubUserId }, null, 2)}'`
        
        // Add to shared selection history
        addSelectionHistory({
          page: 'usp-cancellation-demo',
          action: 'select',
          perkTitle: perk.title,
          perkId: perk.id,
          campaignId: perk.campaignId,
          status: 'error',
          error: errorMessage,
          curlCommand: selectCurlCommand
        })
        return
      }
    } else {
      // If deselecting the perk
      const unselectLogId = addApiLog({
        page: 'usp-cancellation-demo',
        action: 'POST /sdk/v4/usp/{sessionId}/{campaignId}/unselect.json',
        request: {
          url: `https://api.adspostx.com/sdk/v4/usp/${perk.sessionId}/${perk.campaignId}/unselect.json`,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${apiKey}`
          },
          body: { pub_user_id: pubUserId },
          timestamp: new Date().toISOString()
        },
        response: null,
        loading: true,
        error: null
      })

      try {
        console.log('🎯 Calling Unselect Offer API for perk:', perk.title)
        console.log('Session ID:', perk.sessionId)
        console.log('Campaign ID:', perk.campaignId)
        console.log('Offer ID:', perk.offerId)

        // Generate curl command for this request
        const unselectCurlCommand = `curl -X POST \\
  "https://api.adspostx.com/sdk/v4/usp/${perk.sessionId}/${perk.campaignId}/unselect.json" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -d '${JSON.stringify({ pub_user_id: pubUserId }, null, 2)}'`

        // Call the Unselect Offer API
        const unselectResponse = await fetch(`https://api.adspostx.com/sdk/v4/usp/${perk.sessionId}/${perk.campaignId}/unselect.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${apiKey}`
          },
          body: JSON.stringify({
            pub_user_id: pubUserId
          })
        })

        const unselectData = await unselectResponse.json()
        console.log('✅ Unselect Offer API Response:', unselectData)
        console.log('Response Status:', unselectResponse.status, unselectResponse.statusText)
        
        // Update shared API logs with response
        updateApiLog(unselectLogId, {
          response: {
            status: unselectResponse.status,
            statusText: unselectResponse.statusText,
            headers: Object.fromEntries(unselectResponse.headers.entries()),
            data: unselectData,
            timestamp: new Date().toISOString()
          },
          loading: false,
          error: null
        })
        
        if (unselectResponse.ok) {
          console.log('🎉 Offer successfully unselected!')
          showToast(`${perk.title} unselected successfully!`, 'success')
          
          // Add to shared selection history
          addSelectionHistory({
            page: 'usp-cancellation-demo',
            action: 'unselect',
            perkTitle: perk.title,
            perkId: perk.id,
            campaignId: perk.campaignId,
            status: 'success',
            response: unselectData,
            curlCommand: unselectCurlCommand
          })
        } else {
          console.error('❌ Unselect Offer API failed:', unselectData)
          showToast('Failed to unselect offer. Please try again.', 'error')
          
          // Add to shared selection history
          addSelectionHistory({
            page: 'usp-cancellation-demo',
            action: 'unselect',
            perkTitle: perk.title,
            perkId: perk.id,
            campaignId: perk.campaignId,
            status: 'error',
            error: unselectData.message || 'API request failed',
            curlCommand: unselectCurlCommand
          })
          return
        }
      } catch (error) {
        console.error('❌ Error calling Unselect Offer API:', error)
        const errorMessage = error instanceof Error ? error.message : 'Unknown error'
        showToast('Error unselecting offer. Please try again.', 'error')
        
        // Update shared API logs with error
        updateApiLog(unselectLogId, {
          response: null,
          loading: false,
          error: errorMessage
        })
        
        // Generate curl command for error case
        const unselectCurlCommand = `curl -X POST \\
  "https://api.adspostx.com/sdk/v4/usp/${perk.sessionId}/${perk.campaignId}/unselect.json" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -d '${JSON.stringify({ pub_user_id: pubUserId }, null, 2)}'`
        
        // Add to shared selection history
        addSelectionHistory({
          page: 'usp-cancellation-demo',
          action: 'unselect',
          perkTitle: perk.title,
          perkId: perk.id,
          campaignId: perk.campaignId,
          status: 'error',
          error: errorMessage,
          curlCommand: unselectCurlCommand
        })
        return
      }
    }

    // Update the local state
    setSelectedPerks(perks => perks.map(p => 
      p.id === perkId 
        ? { ...p, selected: !p.selected }
        : p
    ))
  }

  // Auto-select function for when usp_all_offers_checked is true
  const autoSelectAllOffers = async (perks: any[], sessionIdToUse: string) => {
    if (perks.length === 0) return

    try {
      console.log('🤖 Auto-selecting all offers via API')
      console.log('Session ID:', sessionIdToUse)
      console.log('Available perks:', perks.map(p => ({ id: p.campaignId, title: p.title })))

      // Get all campaign IDs from available perks
      const campaignIds = perks.map(perk => perk.campaignId).filter(id => id)
      console.log('Campaign IDs to auto-select:', campaignIds)

      // Generate curl command for this request
      const bulkSelectCurlCommand = `curl -X PUT \\
  "https://api.adspostx.com/sdk/v4/usp/session/${sessionIdToUse}.json?pub_user_id=${pubUserId}" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -d '${JSON.stringify({ selected: campaignIds }, null, 2)}'`

      // Call the Bulk Selection API
      const bulkSelectResponse = await fetch(`https://api.adspostx.com/sdk/v4/usp/session/${sessionIdToUse}.json?pub_user_id=${pubUserId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          selected: campaignIds
        })
      })

      const bulkSelectData = await bulkSelectResponse.json()
      console.log('✅ Auto-selection API Response:', bulkSelectData)
      console.log('Response Status:', bulkSelectResponse.status, bulkSelectResponse.statusText)
      
      if (bulkSelectResponse.ok) {
        console.log('🎉 All offers automatically selected!')
        showToast('All offers automatically selected!', 'success')
        
        // Add to shared selection history
        addSelectionHistory({
          page: 'usp-cancellation-demo',
          action: 'bulk_select',
          totalPerks: perks.length,
          status: 'success',
          response: bulkSelectData,
          curlCommand: bulkSelectCurlCommand
        })
        
        // Update local state to mark all perks as selected
        setSelectedPerks(currentPerks => currentPerks.map(p => ({ ...p, selected: true })))
      } else {
        console.error('❌ Auto-selection API failed:', bulkSelectData)
        showToast('Failed to auto-select offers. Please try manual selection.', 'error', 10000, true)
        
        // Add to shared selection history
        addSelectionHistory({
          page: 'usp-cancellation-demo',
          action: 'bulk_select',
          totalPerks: perks.length,
          status: 'error',
          error: bulkSelectData.message || 'API request failed',
          curlCommand: bulkSelectCurlCommand
        })
      }
    } catch (error) {
      console.error('❌ Error calling Auto-selection API:', error)
      showToast('Error auto-selecting offers. Please try manual selection.', 'error')
      
      // Generate curl command for error case
      const campaignIds = perks.map(perk => perk.campaignId).filter(id => id)
      const bulkSelectCurlCommand = `curl -X PUT \\
  "https://api.adspostx.com/sdk/v4/usp/session/${sessionIdToUse}.json?pub_user_id=${pubUserId}" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -d '${JSON.stringify({ selected: campaignIds }, null, 2)}'`
      
      // Add to shared selection history
      addSelectionHistory({
        page: 'usp-cancellation-demo',
        action: 'bulk_select',
        totalPerks: perks.length,
        status: 'error',
        error: error instanceof Error ? error.message : 'Unknown error',
        curlCommand: bulkSelectCurlCommand
      })
    }
  }

  const getSessionDetails = async () => {
    if (!sessionId) {
      showToast('No session ID available', 'error')
      return
    }

    const logId = Date.now().toString()
    
    try {
      console.log('🎯 Calling Get Session Details API')
      console.log('Session ID:', sessionId)
      console.log('Pub User ID:', pubUserId)
      
      // Add to API logs
      const apiRequest = {
        url: `https://api.adspostx.com/sdk/v4/usp/session/${sessionId}.json?pub_user_id=${pubUserId}`,
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${apiKey}`
        },
        timestamp: new Date().toISOString()
      }

      const logId = addApiLog({
        page: 'USP Cancellation Demo',
        action: 'Get Session Details',
        request: apiRequest,
        response: null,
        loading: true,
        error: null
      })
      
      // Store a reference to the log for the sidebar
      const initialLog = {
        id: logId,
        page: 'USP Cancellation Demo',
        action: 'Get Session Details',
        request: apiRequest,
        response: null,
        loading: true,
        error: null,
        timestamp: new Date().toISOString()
      }
      setSessionDetailsLog(initialLog)

      // Call the Get Session Details API
      const sessionDetailsResponse = await fetch(apiRequest.url, {
        method: apiRequest.method,
        headers: apiRequest.headers
      })

      const sessionDetailsData = await sessionDetailsResponse.json()
      console.log('✅ Session Details API Response:', sessionDetailsData)
      console.log('Response Status:', sessionDetailsResponse.status, sessionDetailsResponse.statusText)
      
      // Update the API log with response
      updateApiLog(logId, {
        response: {
          status: sessionDetailsResponse.status,
          statusText: sessionDetailsResponse.statusText,
          headers: Object.fromEntries(sessionDetailsResponse.headers.entries()),
          data: sessionDetailsData,
          timestamp: new Date().toISOString()
        },
        loading: false,
        error: sessionDetailsResponse.ok ? null : sessionDetailsData.message || `Request failed with status ${sessionDetailsResponse.status}`
      })

      // Update sessionDetailsLog state with the updated log
      const updatedLog = {
        id: logId,
        page: 'USP Cancellation Demo',
        action: 'Get Session Details',
        request: apiRequest,
        response: {
          status: sessionDetailsResponse.status,
          statusText: sessionDetailsResponse.statusText,
          headers: Object.fromEntries(sessionDetailsResponse.headers.entries()),
          data: sessionDetailsData,
          timestamp: new Date().toISOString()
        },
        loading: false,
        error: sessionDetailsResponse.ok ? null : sessionDetailsData.message || `Request failed with status ${sessionDetailsResponse.status}`,
        timestamp: new Date().toISOString()
      }
      setSessionDetailsLog(updatedLog)
      
      if (sessionDetailsResponse.ok) {
        console.log('🎉 Session details retrieved successfully!')
        showToast('Session details retrieved successfully!', 'success')
      } else {
        console.error('❌ Session Details API failed:', sessionDetailsData)
        showToast('Failed to get session details. Please try again.', 'error')
      }
    } catch (error) {
      console.error('❌ Error calling Session Details API:', error)
      showToast('Error getting session details. Please try again.', 'error')
      
      // Update log with error
      updateApiLog(logId, {
        loading: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      })
    }
  }

  // Don't render anything if credentials are being checked
  if (!apiKey || !pubUserId) {
    return null
  }
  
  return (
    <div className="min-h-screen">
      <div className="flex flex-col min-h-screen bg-background-light">
        {/* Top header */}
        <header className="sticky top-0 z-10 bg-white border-b border-gray-200 shadow-sm">
          <div className="container mx-auto p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-primary-500 rounded-ms flex items-center justify-center">
                <span className="text-white font-bold text-sm">S</span>
              </div>
              <h1 className="text-xl font-semibold text-text-primary font-sans">
                StoreFlow
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="text-sm px-4 py-2 bg-gray-600 text-white rounded-ms hover:bg-gray-700 transition-colors font-medium"
              >
                Return to Home
              </button>
              <button
                onClick={() => setShowApiPanel(!showApiPanel)}
                className={`flex items-center gap-2 px-3 py-2 rounded-ms transition-colors font-medium ${
                  showApiPanel 
                    ? 'bg-primary-500 text-white shadow-md' 
                    : 'bg-primary-100 text-primary-600 hover:bg-primary-200 border border-primary-300'
                }`}
                title="Toggle API Monitor"
              >
                <Code size={16} />
                <span className="text-sm">API Monitor</span>
              </button>
            </div>
          </div>
        </header>

        <main className="flex-grow container mx-auto p-3 max-w-6xl">
          {/* Introduction section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-4 text-center"
          >
            <div className="mb-4">
              <h2 className="text-2xl font-bold text-text-primary mb-3 font-sans">
                Wait! We Value You As A Customer
              </h2>
              <p className="text-body text-text-neutral max-w-3xl mx-auto mb-3 font-sans">
                Before you cancel, we'd like to show our appreciation with some exclusive offers. 
                Select any that interest you and we'll deliver them straight to your email within 24 hours.
              </p>
              <p className="text-sm text-gray-600 max-w-2xl mx-auto font-sans">
                No strings attached - these offers are yours to keep, even if you decide to cancel your account.
              </p>
            </div>
          </motion.div>

          {/* Special offers section */}
          <div className="mb-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-center mb-4"
            >
              <h2 className="text-xl font-bold text-text-primary mb-2 font-sans">
                Select Your Exclusive Offers
              </h2>
              <p className="text-sm text-text-neutral max-w-2xl mx-auto font-sans mb-2">
                Choose any offers below and we'll email them to you within 24 hours. No purchase necessary.
              </p>
              <p className="text-sm text-text-neutral font-sans">
                Select any of these special deals to continue your membership with extra benefits
              </p>
            </motion.div>

            {perksLoading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex justify-center py-12"
              >
                <div className="flex flex-col items-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  <p className="text-gray-600 mt-4 font-sans">Loading special offers...</p>
                </div>
              </motion.div>
            )}

            {perksError && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-red-50 border border-red-200 rounded-card p-4 mb-6"
              >
                <p className="text-red-800 font-sans">
                  Failed to load special offers. Please try again.
                </p>
                <button
                  onClick={() => fetchOffersAndRestoreState(true)}
                  className="mt-2 text-red-700 hover:text-red-900 underline font-sans"
                >
                  Retry
                </button>
              </motion.div>
            )}

            {/* Special message when all offers are automatically checked */}
            {uspAllOffersChecked && selectedPerks.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-green-50 border border-green-200 rounded-card p-4 mb-6"
              >
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0">
                    <Check className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="text-green-800 font-medium font-sans">
                      All offers pre-selected for you!
                    </h4>
                    <p className="text-green-700 text-sm mt-1 font-sans">
                      We've automatically selected all available offers based on your preferences. These will be emailed to you even if you cancel. Feel free to unselect any you don't want.
                    </p>
                  </div>
                </div>
              </motion.div>
            )}

            {!perksLoading && !perksError && selectedPerks.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8 max-w-6xl mx-auto">
                {selectedPerks.map((perk, index) => {
                  const category = getOfferCategory(perk);
                  const IconComponent = getOfferIcon(category);
                  const categoryColor = getCategoryColor(category);

                  return (
                    <motion.div
                      key={perk.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className={`bg-white rounded-card shadow-card border-2 transition-all duration-200 cursor-pointer hover:shadow-card-hover hover:-translate-y-1
                        ${perk.selected 
                          ? 'border-primary-500 bg-primary-50 shadow-primary-100' 
                          : 'border-gray-200 hover:border-primary-300'}`}
                      onClick={() => togglePerk(perk.id)}
                    >
                      <div className="p-4 flex flex-col h-full text-center min-h-[220px]">
                        {/* Header with icon and selection status - centered */}
                        <div className="flex flex-col items-center mb-4 relative">
                          {/* Selection indicator - positioned absolutely */}
                          {perk.selected && (
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className="absolute top-0 right-0 text-primary-600"
                            >
                              <Check size={20} />
                            </motion.div>
                          )}
                          
                          {/* Category icon and image centered */}
                          <div className="flex items-center justify-center space-x-3 mb-3">
                            {/* Category icon */}
                            <div className={`p-3 rounded-ms ${categoryColor}`}>
                              <IconComponent size={20} />
                            </div>
                            
                            {/* Small image if available */}
                            {perk.image && (
                              <div className="w-16 h-16 rounded-ms overflow-hidden bg-gray-100">
                                <img
                                  src={perk.image}
                                  alt={perk.title}
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                  onError={(e) => {
                                    // If image fails to load, hide it
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Title - emphasized and centered */}
                        <h3 className="font-bold text-lg text-text-primary mb-2 leading-tight min-h-[2.5rem] flex items-center justify-center font-sans">
                          {perk.title}
                        </h3>
                        
                        {/* Description - centered and prominent */}
                        <p className="text-text-neutral text-sm leading-relaxed mb-4 flex-grow text-center max-w-sm mx-auto font-sans">
                          {perk.description}
                        </p>
                        
                        <div className="mt-auto">
                          <button
                            onClick={() => togglePerk(perk.id)}
                            className={`w-full py-4 px-6 rounded-xl text-base font-semibold transition-all duration-200 shadow-sm
                              ${perk.selected 
                                ? 'bg-blue-500 text-white hover:bg-blue-600 shadow-lg' 
                                : 'border-2 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-blue-300 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/10'}`}
                          >
                            {perk.selected ? '✓ Selected' : 'Select This Offer'}
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Bottom action area */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg shadow-sm border border-blue-200 mt-4">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-1">
                  Thanks for being a valued customer!
                </h3>
                <p className="text-text-neutral font-sans">
                  Selected offers will be delivered to your email within 24 hours, even if you cancel.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={() => {
                    const selectedCount = selectedPerks.filter((p: any) => p.selected).length;
                    if (selectedCount === 0) {
                      showToast('Please select at least one offer before proceeding.', 'error', 4000);
                      return;
                    }
                    setConfirmationModalData({
                      type: 'complete-cancellation',
                      selectedCount
                    });
                    setShowConfirmationModal(true);
                  }}
                  className="px-5 py-2 border border-gray-300 rounded-ms text-text-neutral hover:bg-gray-100 transition-colors font-medium"
                >
                  Complete Cancellation
                </button>
                <button
                  onClick={() => {
                    const selectedCount = selectedPerks.filter((p: any) => p.selected).length;
                    if (selectedCount === 0) {
                      showToast('Please select at least one offer before proceeding.', 'error', 4000);
                      return;
                    }
                    setConfirmationModalData({
                      type: 'keep-account',
                      selectedCount
                    });
                    setShowConfirmationModal(true);
                  }}
                  className="px-5 py-2 bg-primary-600 text-white rounded-ms hover:bg-primary-700 transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  <Check size={18} />
                  Send Me My Offers & Keep Account
                </button>
              </div>
            </div>
          </div>
        </main>

        {/* API Panel Sidebar */}
        <motion.div
          initial={{ x: '100%' }}
          animate={{ x: showApiPanel ? 0 : '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="fixed top-0 right-0 h-full bg-white shadow-2xl border-l border-gray-200 z-50 overflow-hidden"
          style={{ width: `${sidebarWidth}px` }}
        >
          {/* Resize Handle */}
          <div
            className={`absolute left-0 top-0 w-1 h-full bg-gray-300 hover:bg-primary-500 cursor-ew-resize transition-colors z-10 ${
              isResizing ? 'bg-primary-500' : ''
            }`}
            onMouseDown={handleMouseDown}
            title="Drag to resize sidebar"
          />
          
          {/* Resize Handle Visual Indicator */}
          <div
            className={`absolute left-0.5 top-1/2 transform -translate-y-1/2 w-0.5 h-8 bg-white rounded-full opacity-60 pointer-events-none ${
              isResizing ? 'opacity-100' : ''
            }`}
          />

          <div className="flex flex-col h-full">
            {/* Panel Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <Code className="w-5 h-5 text-primary-600" />
                <h3 className="font-semibold text-text-primary font-sans">API Monitor</h3>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowApiPanel(false)}
                  className="p-1 hover:bg-gray-100 rounded-ms"
                >
                  <Eye className="w-5 h-5 text-text-neutral" />
                </button>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setActiveTab('session')}
                className={`flex-1 px-3 py-3 text-sm font-medium transition-colors font-sans ${
                  activeTab === 'session'
                    ? 'bg-primary-50 text-primary-600 border-b-2 border-primary-500'
                    : 'text-text-neutral hover:text-text-primary'
                }`}
              >
                Session API
              </button>
              <button
                onClick={() => setActiveTab('general')}
                className={`flex-1 px-3 py-3 text-sm font-medium transition-colors font-sans ${
                  activeTab === 'general'
                    ? 'bg-primary-50 text-primary-600 border-b-2 border-primary-500'
                    : 'text-text-neutral hover:text-text-primary'
                }`}
              >
                Moments API
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`flex-1 px-3 py-3 text-sm font-medium transition-colors font-sans ${
                  activeTab === 'history'
                    ? 'bg-primary-50 text-primary-600 border-b-2 border-primary-500'
                    : 'text-text-neutral hover:text-text-primary'
                }`}
              >
                Selection History
              </button>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {activeTab === 'session' ? (
                <div className="space-y-4">
                  {/* Session API Tab Content */}
                  
                  {/* Illustration/Guide when no session data is available */}
                  {!sessionDetailsLog && (
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-6 border border-blue-200 dark:border-blue-800">
                      <div className="text-center mb-4">
                        <div className="relative mx-auto w-16 h-16 mb-4">
                          {/* Animated pointer illustration */}
                          <div className="absolute inset-0 bg-blue-500 rounded-full opacity-20 animate-ping"></div>
                          <div className="relative w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center">
                            <Activity className="w-8 h-8 text-white" />
                          </div>
                        </div>
                        <h4 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">
                          Discover Session Insights
                        </h4>
                        <p className="text-blue-700 dark:text-blue-300 text-sm mb-4">
                          Click the button below to fetch detailed information about your current session, including campaigns, device info, and user statistics.
                        </p>
                        
                        {/* Feature highlights */}
                        <div className="grid grid-cols-2 gap-3 text-left mb-4">
                          <div className="flex items-center space-x-2 text-xs text-blue-600 dark:text-blue-400">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            <span>Campaign Analytics</span>
                          </div>
                          <div className="flex items-center space-x-2 text-xs text-blue-600 dark:text-blue-400">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            <span>Device Information</span>
                          </div>
                          <div className="flex items-center space-x-2 text-xs text-blue-600 dark:text-blue-400">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            <span>Selection Statistics</span>
                          </div>
                          <div className="flex items-center space-x-2 text-xs text-blue-600 dark:text-blue-400">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            <span>Location Data</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="text-center">
                    <button
                      onClick={getSessionDetails}
                      disabled={!sessionId}
                      className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors shadow-lg hover:shadow-xl"
                    >
                      <Activity className="w-4 h-4" />
                      <span>Get Session Details</span>
                    </button>
                  </div>

                  {/* Session details display */}
                  {sessionDetailsLog && (
                    <div className="space-y-4">
                      <h4 className="font-medium text-gray-900 dark:text-white">Session Information</h4>
                      
                      {/* Request */}
                      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                        <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Request</h5>
                        <pre className="text-xs bg-background-section p-2 rounded-ms border overflow-x-auto font-mono">
                          {JSON.stringify(sessionDetailsLog.request, null, 2)}
                        </pre>
                      </div>

                      {/* Response */}
                      {sessionDetailsLog.response && (
                        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                          <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Response</h5>
                          <pre className="text-xs bg-background-section p-2 rounded-ms border overflow-x-auto max-h-96 font-mono">
                            {JSON.stringify(sessionDetailsLog.response.data, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : activeTab === 'general' ? (
                <div className="space-y-4">
                  <h4 className="font-medium text-text-primary font-sans">API Logs</h4>
                  <p className="text-sm text-text-neutral font-sans">
                    View API requests and responses from this session.
                  </p>
                  
                  {/* API Logs from context */}
                  {contextApiLogs && contextApiLogs.length > 0 ? (
                    <div className="space-y-3">
                      {contextApiLogs
                        .filter(log => log.page === 'USP Cancellation Demo')
                        .reverse()
                        .map((log) => (
                        <div key={log.id} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              {log.action}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {new Date(log.timestamp).toLocaleTimeString()}
                            </span>
                          </div>
                          
                          {/* Request */}
                          <div className="mb-2">
                            <h6 className="text-xs font-medium text-text-neutral mb-1">Request</h6>
                            <div className="bg-background-section rounded-ms p-2 text-xs font-mono">
                              <div className="text-primary-600">{log.request.method} {log.request.url}</div>
                              {log.request.body && (
                                <pre className="mt-1 text-text-neutral overflow-x-auto">
                                  {JSON.stringify(log.request.body, null, 2)}
                                </pre>
                              )}
                            </div>
                          </div>

                          {/* Response */}
                          {log.response && (
                            <div>
                              <h6 className="text-xs font-medium text-text-neutral mb-1">Response</h6>
                              <div className="bg-background-section rounded-ms p-2 text-xs font-mono">
                                <div className={`mb-1 ${
                                  log.response.status >= 200 && log.response.status < 300 
                                    ? 'text-green-600' 
                                    : 'text-red-600'
                                }`}>
                                  {log.response.status} {log.response.statusText}
                                </div>
                                <pre className="text-text-neutral overflow-x-auto max-h-32">
                                  {JSON.stringify(log.response.data, null, 2)}
                                </pre>
                              </div>
                            </div>
                          )}

                          {/* Error */}
                          {log.error && (
                            <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 rounded text-xs text-red-600 dark:text-red-400">
                              Error: {log.error}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
                      <p className="text-gray-500 dark:text-gray-400 text-sm">
                        API logs will appear here as you interact with offers.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <h4 className="font-medium text-text-primary font-sans">Selection History</h4>
                  <p className="text-sm text-text-neutral font-sans">
                    Track your offer selection and deselection actions.
                  </p>
                  
                  {/* Selection History from context */}
                  {contextSelectionHistory && contextSelectionHistory.length > 0 ? (
                    <div className="space-y-3">
                      {/* Debug info */}
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                        Total entries: {contextSelectionHistory.length} | 
                        This page: {contextSelectionHistory.filter(entry => entry.page === 'usp-cancellation-demo').length}
                      </div>
                      {contextSelectionHistory
                        .reverse()
                        .map((entry) => (
                        <div key={entry.id} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <span className={`w-2 h-2 rounded-full ${
                                entry.status === 'success' ? 'bg-green-500' : 'bg-red-500'
                              }`}></span>
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                {entry.action === 'select' ? 'Selected' : 
                                 entry.action === 'unselect' ? 'Unselected' :
                                 entry.action === 'bulk_select' ? 'Bulk Selected' :
                                 'Bulk Unselected'}
                              </span>
                            </div>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {new Date(entry.timestamp).toLocaleTimeString()}
                            </span>
                          </div>
                          
                          {entry.perkTitle && (
                            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                              Offer: {entry.perkTitle}
                            </div>
                          )}
                          
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                            Page: {entry.page}
                          </div>
                          
                          {entry.totalPerks && (
                            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                              Total Perks: {entry.totalPerks}
                            </div>
                          )}

                          {entry.error && (
                            <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 rounded text-xs text-red-600 dark:text-red-400">
                              Error: {entry.error}
                            </div>
                          )}

                          {entry.response && (
                            <details className="mt-2">
                              <summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">
                                View Response
                              </summary>
                              <pre className="mt-1 text-xs bg-background-section p-2 rounded-ms border overflow-x-auto max-h-32 font-mono">
                                {JSON.stringify(entry.response, null, 2)}
                              </pre>
                            </details>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
                      <p className="text-gray-500 dark:text-gray-400 text-sm">
                        Selection history will appear here as you select offers.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Toasts */}
        <div className="fixed top-4 right-4 z-50 space-y-3 max-w-sm">
          {toasts.map(toast => (
            <motion.div
              key={toast.id}
              initial={{ opacity: 0, y: -30, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -30, scale: 0.9 }}
              className={`p-4 rounded-md shadow-lg flex items-start ${
                toast.type === 'success' ? 'bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
                toast.type === 'error' ? 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300' :
                toast.type === 'info' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300' :
                'bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-300'
              }`}
            >
              <div className="flex-shrink-0 mr-3">
                {toast.type === 'success' && <CheckCircle className="h-5 w-5" />}
                {toast.type === 'error' && <XCircle className="h-5 w-5" />}
                {toast.type === 'info' && <Info className="h-5 w-5" />}
              </div>
              <div className="flex-1">
                <p className="text-sm">{toast.message}</p>
              </div>
              <button
                onClick={() => dismissToast(toast.id)}
                className="ml-3 flex-shrink-0 hover:text-gray-500 dark:hover:text-gray-400"
              >
                <X className="h-4 w-4" />
              </button>
            </motion.div>
          ))}
        </div>

        {/* Tutorial modal */}
        {showTutorial && (
          <DemoTutorial 
            onClose={() => {
              setShowTutorial(false)
              localStorage.setItem('usp_tutorial_seen', 'true')
            }} 
          />
        )}

        {/* Confirmation Modal */}
        {showConfirmationModal && confirmationModalData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-card shadow-xl max-w-md w-full p-6"
            >
              <div className="text-center">
                <div className="mb-4">
                  {confirmationModalData.type === 'keep-account' ? (
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Check className="w-8 h-8 text-green-600" />
                    </div>
                  ) : (
                    <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Info className="w-8 h-8 text-primary-600" />
                    </div>
                  )}
                </div>

                <h3 className="text-xl font-bold text-text-primary mb-2 font-sans">
                  {confirmationModalData.type === 'keep-account' 
                    ? 'Account Maintained! 🎉' 
                    : 'Cancellation Confirmed'}
                </h3>

                <div className="text-text-neutral mb-6 font-sans">
                  {confirmationModalData.selectedCount > 0 ? (
                    <div>
                      <p className="mb-2">
                        <strong>{confirmationModalData.selectedCount} exclusive offer{confirmationModalData.selectedCount > 1 ? 's' : ''}</strong> will be delivered to your email within 24 hours.
                      </p>
                      <p className="text-sm">
                        {confirmationModalData.type === 'keep-account' 
                          ? 'Your account benefits continue, plus you\'ll receive these special offers!'
                          : 'Even though your account is cancelled, these offers are yours to keep as a thank you.'}
                      </p>
                    </div>
                  ) : (
                    <p>
                      {confirmationModalData.type === 'keep-account' 
                        ? 'Great choice! Your account and all benefits remain active.'
                        : 'Your account has been cancelled. We hope to see you again soon!'}
                    </p>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => {
                      setShowConfirmationModal(false);
                      setConfirmationModalData(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-ms text-text-neutral hover:bg-gray-50 transition-colors font-medium"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      setShowConfirmationModal(false);
                      setConfirmationModalData(null);
                      // Show final toast and navigate
                      if (confirmationModalData.type === 'keep-account') {
                        showToast('Account maintained successfully! Check your email for offers.', 'success', 5000);
                      } else {
                        showToast('Account cancelled. Offers will be emailed shortly.', 'info', 5000);
                      }
                      setTimeout(() => navigate('/'), 2000);
                    }}
                    className={`px-4 py-2 rounded-ms font-medium transition-colors ${
                      confirmationModalData.type === 'keep-account'
                        ? 'bg-green-600 hover:bg-green-700 text-white'
                        : 'bg-primary-600 hover:bg-primary-700 text-white'
                    }`}
                  >
                    {confirmationModalData.type === 'keep-account' ? 'Continue Shopping' : 'Return Home'}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  )
}

export default USPCancellationDemoPage
